import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useLocation, useNavigate } from 'react-router';

import type { ForumPost, ForumAuthor as TForumAuthor } from '~/api/forum/types';
import type { EditorRefHandle } from '~/components';

import { useForumPost } from '~/api/forum/useForumPost';
import { useUser } from '~/api/user';
import { AlertDialog, Button, Card, Dialog, Header, Icon, Menu, Redirect, RichTextRenderer, Separator, Tag } from '~/components';
import { EnableCommunityProfileDialog } from '~/features/community';
import { illustrationForForumTag } from '~/pages/Forum/Forum.utils';
import { routes } from '~/providers/RouterProvider/router.routes';

import type { loader } from './ForumPostDetail.loader';

import { ForumAuthor } from '../components/ForumAuthor';
import { ForumAuthorDialog } from '../components/ForumAuthorDialog';
import { ForumHeaderPortal } from '../components/ForumHeaderPortal';
import { LikeForumPostButton } from '../components/LikeForumPostButton';
import { SaveForumPostButton } from '../components/SaveForumPostButton';
import { ConfirmDeleteForumPostDialog } from '../ConfirmDeleteForumPostDialog/ConfirmDeleteForumPostDialog';
import { EditForumPostForm } from '../EditForumPostForm/EditForumPostForm';
import { ForumComments } from './ForumComments';
import { commentsStyle, containerStyle, contentStyle, footerActionsStyle, footerStyle, onTopStyle, postStyle } from './ForumPostDetail.css';

export const ForumPostDetailGuard = () => {
  const loaderData = useLoaderData<typeof loader>();
  const { data: forumPost } = useForumPost(loaderData.id);

  if (!forumPost) return <Redirect to={routes.forumTimeline} />;

  return <ForumPostDetail forumPost={forumPost} />;
};

type Props = {
  forumPost: ForumPost;
};

const ForumPostDetail = ({ forumPost }: Props) => {
  const { data: user } = useUser();

  const commentEditorRef = useRef<EditorRefHandle>(null);

  const [mode, setMode] = useState<'read' | 'edit'>('read');
  const confirmDeleteHandle = AlertDialog.useHandle();
  const enableCommunityProfileHandle = Dialog.useHandle();
  const authorDialogHandle = Dialog.useHandle<TForumAuthor>();

  const { state } = useLocation();
  const from = (state?.from as string | undefined) ?? routes.forumTimeline;

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'forum']);

  const isSelfPost = forumPost.author.communityProfileId === user.communityProfile.id;

  const onComment = () => {
    if (!user.communityProfile.enabled) {
      return enableCommunityProfileHandle.open(null);
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: document.documentElement.scrollHeight });
      commentEditorRef.current?.focus();
    });
  };

  return (
    <>
      <ForumHeaderPortal>
        <Header.Root>
          <Header.Content backTo={from} title={forumPost.title} />

          <Header.Actions>
            {!forumPost.locked && (
              <Header.Action icon="ComicBubble" onClick={onComment}>
                {t('forum:post.card.actions.comment')}
              </Header.Action>
            )}

            {isSelfPost && (
              <Menu.Root>
                <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} icon="MoreVert" />} />

                <Menu.List>
                  <Menu.Item icon="Edit" onClick={() => setMode('edit')}>
                    {t('common:actions.edit')}
                  </Menu.Item>

                  <Menu.Item icon="Trash" onClick={() => confirmDeleteHandle.open(null)}>
                    {t('common:actions.delete')}
                  </Menu.Item>
                </Menu.List>
              </Menu.Root>
            )}
          </Header.Actions>
        </Header.Root>
      </ForumHeaderPortal>

      <div className={containerStyle}>
        <div className={contentStyle}>
          <Card.Root className={postStyle} render={<article />} variant="primary-light">
            {mode === 'read' && <RichTextRenderer content={forumPost.content} />}

            {mode === 'edit' && <EditForumPostForm forumPost={forumPost} onFinish={() => setMode('read')} />}

            <ForumAuthor
              author={forumPost.author}
              createdAt={forumPost.createdAt}
              dialogHandle={authorDialogHandle}
              editedAt={forumPost.editedAt}
            />

            <Separator />

            <footer className={footerStyle}>
              <Tag illustration={illustrationForForumTag(forumPost.tag)} text={t(`forum:tags.${forumPost.tag}.name`)} />

              <div className={footerActionsStyle}>
                <LikeForumPostButton likes={forumPost.likes} postId={forumPost.id} size="large" />

                <div className={commentsStyle}>
                  <Icon aria-label={t('forum:post.card.actions.comments', { count: forumPost.numberOfComments })} name="ComicBubble" />

                  <span aria-hidden>{forumPost.numberOfComments}</span>
                </div>

                <div className={onTopStyle}>
                  <SaveForumPostButton postId={forumPost.id} saved={forumPost.saved} />
                </div>
              </div>
            </footer>
          </Card.Root>

          <AlertDialog.Root handle={confirmDeleteHandle}>
            <ConfirmDeleteForumPostDialog
              forumPost={forumPost}
              onClose={() => confirmDeleteHandle.close()}
              onDeleted={() => navigate(from, { replace: true })}
            />
          </AlertDialog.Root>

          <ForumComments
            authorDialogHandle={authorDialogHandle}
            commentEditorRef={commentEditorRef}
            forumPost={forumPost}
            onEnableCommunityProfile={() => enableCommunityProfileHandle.open(null)}
          />

          <Dialog.Root handle={enableCommunityProfileHandle}>
            <EnableCommunityProfileDialog />
          </Dialog.Root>

          <ForumAuthorDialog handle={authorDialogHandle} />
        </div>
      </div>
    </>
  );
};
